import { jsx } from 'react/jsx-runtime';
import { useVirtualizer } from '@tanstack/react-virtual';
import { forwardRef, useRef, useMemo } from 'react';
import { styled } from 'styled-components';
import { veryBigCodeSize, lowlightMimeTypeToLangMap } from '../code.config.js';
import { mergeRefs } from 'react-merge-refs';
import { useCodeLineStyling } from '../hooks/useCodeLineHeight.js';
import { useQueryCodeLowlightHTML } from '../queries/useQueryCodeLowlightHTML.js';
import { createPlaceholderChunks } from '../utils/ast.js';
import { CodeRenderChunk } from './CodeRenderChunk.js';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { omit } from '@fabric-app/utils/object/manipulation';

const Wrapper = styled.div.withConfig({
  displayName: "CodeRender__Wrapper",
  componentId: "sc-b66e8ae-0"
})([
  `display:flex;flex-grow:1;flex-shrink:1;overflow:auto;position:relative;white-space:pre-wrap;word-wrap:break-word;> pre{margin:0;font-family:'Courier Prime',monospace;white-space:pre-wrap;}&& pre code.hljs{font-family:'Courier Prime',monospace;*{font-family:inherit;}display:block;padding:0;white-space:pre-wrap;background:none;.code-placeholder{color:`,
  `;}}`,
  ``
], cssVar["color-text-tertiary"], ({ overflow }) => overflow && `
    overflow: ${overflow};
    && pre, && pre code.hljs {
      overflow: ${overflow};
    }
  `);
const Loader = styled.div.attrs({
  children: /* @__PURE__ */ jsx(Spinner, {})
}).withConfig({
  displayName: "CodeRender__Loader",
  componentId: "sc-b66e8ae-1"
})([
  `display:flex;justify-content:center;align-items:center;height:100%;width:100%;`
]);
const CodeRender = /* @__PURE__ */ forwardRef(({ truncate, mimeType, scrollRoot, code, ...props }, propRef) => {
  const wrapperRef = useRef(null);
  const ref = useMemo(() => mergeRefs([
    propRef,
    wrapperRef
  ]), [
    propRef
  ]);
  const truncatedCode = useMemo(() => {
    if (truncate && code.length > truncate) {
      return code.slice(0, truncate);
    }
    return code;
  }, [
    code,
    truncate
  ]);
  const isTooLarge = truncatedCode.length > veryBigCodeSize;
  const { data, isLoading } = useQueryCodeLowlightHTML({
    code: truncatedCode,
    lang: lowlightMimeTypeToLangMap[mimeType ?? ""]
  }, {
    enabled: true
  });
  const chunks = useMemo(() => data?.chunks ?? (!isTooLarge ? createPlaceholderChunks(code, 100) : []), [
    data?.chunks,
    isTooLarge,
    code
  ]);
  const { lineHeight } = useCodeLineStyling(wrapperRef.current);
  const rowVirtualizer = useVirtualizer({
    count: chunks.length ?? 0,
    getScrollElement: () => scrollRoot ?? wrapperRef.current ?? null,
    estimateSize: (index) => (chunks[index].numberOfLines ?? 0) * lineHeight,
    overscan: 3,
    debug: true
  });
  return /* @__PURE__ */ jsx(Wrapper, {
    ref,
    ...omit(props, [
      "code",
      "url"
    ]),
    children: isLoading && isTooLarge ? /* @__PURE__ */ jsx(Loader, {}) : /* @__PURE__ */ jsx("pre", {
      "data-openreplay-label": "CodeRender",
      style: {
        height: rowVirtualizer.getTotalSize()
      },
      children: rowVirtualizer.getVirtualItems().map(({ index, key, start }) => /* @__PURE__ */ jsx(CodeRenderChunk, {
        ast: chunks[index].ast,
        ref: rowVirtualizer.measureElement,
        top: start - rowVirtualizer.options.scrollMargin,
        "data-index": index
      }, key))
    })
  });
});
CodeRender.displayName = "CodeRender";

export { CodeRender };
//# sourceMappingURL=CodeRender.js.map
